import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import BookSearchPreview from './BookSearchPreview'
import { useBookSearch, BookSearchResult } from '../hooks/useBookSearch'
import Modal from './Modal'

interface BookPickerProps {
  clubId: number
  onClose: () => void
  onSuccess: () => void
  preferredProvider?: string
}

export default function BookPicker({ clubId, onClose, onSuccess, preferredProvider }: BookPickerProps) {
  const { searchQuery, setSearchQuery, results, loading, error: searchError } = useBookSearch()
  const [selectedBook, setSelectedBook] = useState<BookSearchResult | null>(null)
  const [previewBook, setPreviewBook] = useState<BookSearchResult | null>(null)
  const [pickingBook, setPickingBook] = useState(false)
  const [error, setError] = useState('')
  const [manualEntry, setManualEntry] = useState(false)
  const [manualBook, setManualBook] = useState({ title: '', author: '' })
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [readingDeadline, setReadingDeadline] = useState('')

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [results])

  useEffect(() => {
    if (searchError) {
      setError(searchError)
    }
  }, [searchError])

  const handlePickBook = async (book: BookSearchResult) => {
    if (!readingDeadline) {
      setError('Please set a reading deadline')
      return
    }

    setPickingBook(true)
    setError('')

    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/book_picks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_pick: {
            status: 'current',
            started_at: new Date().toISOString().split('T')[0],
            reading_deadline: readingDeadline,
            book_attributes: {
              title: book.title,
              author: book.author,
              isbn: book.isbn,
              description: book.description,
              cover_url: book.cover_url,
              first_publish_year: book.first_publish_year,
              number_of_pages: book.number_of_pages,
              metadata: book.metadata
            }
          }
        })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.errors?.join(', ') || 'Failed to pick book')
      }
    } catch (err) {
      setError('Failed to pick book')
    } finally {
      setPickingBook(false)
    }
  }

  const handleManualSubmit = async () => {
    if (!manualBook.title || !manualBook.author) {
      setError('Title and author are required')
      return
    }

    await handlePickBook({
      title: manualBook.title,
      author: manualBook.author
    })
  }

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={true}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Pick a Book</h2>
            <p className="text-gray-600">Search for a book or enter details manually</p>
          </div>
        </div>

        {/* Search / Manual Toggle */}
        <div className="px-6 pt-4">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setManualEntry(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                !manualEntry
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Search Books
            </button>
            <button
              onClick={() => setManualEntry(true)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                manualEntry
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Enter Manually
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {selectedBook ? (
            /* Book Confirmation with Deadline */
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-xl">
                {selectedBook.cover_url ? (
                  <img
                    src={selectedBook.cover_url}
                    alt={selectedBook.title}
                    className="w-20 h-30 object-cover rounded shadow-sm"
                  />
                ) : (
                  <div className="w-20 h-30 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                    <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{selectedBook.title}</h3>
                  <p className="text-gray-600 mb-2">by {selectedBook.author}</p>
                  {selectedBook.description && (
                    <p className="text-sm text-gray-500 line-clamp-3">{selectedBook.description}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="reading-deadline" className="block text-sm font-medium text-gray-700 mb-2">
                  Reading Deadline *
                </label>
                <input
                  id="reading-deadline"
                  type="date"
                  value={readingDeadline}
                  onChange={(e) => setReadingDeadline(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  autoFocus
                />
                <p className="mt-2 text-xs text-gray-500">
                  Set a deadline for when everyone should finish reading this book
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setSelectedBook(null)
                    setReadingDeadline('')
                    setError('')
                  }}
                  className="flex-1 px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300"
                >
                  Back
                </button>
                <button
                  onClick={() => handlePickBook(selectedBook)}
                  disabled={pickingBook || !readingDeadline}
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                >
                  {pickingBook ? 'Picking book...' : 'Confirm Pick'}
                </button>
              </div>
            </div>
          ) : !manualEntry ? (
            <>
              {/* Search Input */}
              <div className="mb-6">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (results.length === 0) return

                    if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      setHighlightedIndex((prev) =>
                        prev < results.length - 1 ? prev + 1 : prev
                      )
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault()
                      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
                    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
                      e.preventDefault()
                      setSelectedBook(results[highlightedIndex])
                    }
                  }}
                  placeholder="Search by title or author..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  autoFocus
                />
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              )}

              {/* Search Results */}
              {!loading && results.length > 0 && (
                <div className="space-y-3">
                  {results.map((book, index) => (
                    <div
                      key={index}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`w-full bg-white border-2 rounded-xl p-4 transition-all ${
                        highlightedIndex === index
                          ? 'border-indigo-500 shadow-md bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-400 hover:shadow-md'
                      }`}
                    >
                      <button
                        onClick={() => setPreviewBook(book)}
                        className="flex flex-col sm:flex-row gap-4 w-full text-left"
                      >
                        {book.cover_url ? (
                          <img
                            src={book.cover_url}
                            alt={book.title}
                            className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-1">{book.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">by {book.author}</p>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                            {book.first_publish_year && <span>{book.first_publish_year}</span>}
                            {book.number_of_pages && (
                              <>
                                <span>•</span>
                                <span>{book.number_of_pages} pages</span>
                              </>
                            )}
                            {book.isbn && (
                              <>
                                <span>•</span>
                                <span>ISBN: {book.isbn}</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-indigo-600 mt-1">Click to see details</p>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {!loading && searchQuery.length >= 2 && results.length === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No books found</h3>
                  <p className="text-gray-600 mb-4">Try a different search or enter details manually</p>
                  <button
                    onClick={() => setManualEntry(true)}
                    className="text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Enter book manually →
                  </button>
                </div>
              )}

              {!loading && searchQuery.length < 2 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-gray-600">Start typing to search for books</p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Manual Entry Form */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="manual-title" className="block text-sm font-medium text-gray-700 mb-2">
                    Book Title *
                  </label>
                  <input
                    id="manual-title"
                    type="text"
                    value={manualBook.title}
                    onChange={(e) => setManualBook({ ...manualBook, title: e.target.value })}
                    placeholder="Enter book title"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    autoFocus
                  />
                </div>

                <div>
                  <label htmlFor="manual-author" className="block text-sm font-medium text-gray-700 mb-2">
                    Author *
                  </label>
                  <input
                    id="manual-author"
                    type="text"
                    value={manualBook.author}
                    onChange={(e) => setManualBook({ ...manualBook, author: e.target.value })}
                    placeholder="Enter author name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="manual-deadline" className="block text-sm font-medium text-gray-700 mb-2">
                    Reading Deadline *
                  </label>
                  <input
                    id="manual-deadline"
                    type="date"
                    value={readingDeadline}
                    onChange={(e) => setReadingDeadline(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Set a deadline for when everyone should finish reading this book
                  </p>
                </div>

                <button
                  onClick={handleManualSubmit}
                  disabled={pickingBook || !manualBook.title || !manualBook.author || !readingDeadline}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                >
                  {pickingBook ? 'Picking book...' : 'Pick This Book'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Book Preview Modal */}
      {previewBook && (
        <BookSearchPreview
          book={previewBook}
          onClose={() => setPreviewBook(null)}
          onSelect={() => {
            setSelectedBook(previewBook)
            setPreviewBook(null)
          }}
          preferredProvider={preferredProvider}
          showSelectButton={true}
        />
      )}
    </>
  )
}
